/**
 * A ponte entre o avaliador (puro) e o `dataSource` do Affine.
 *
 * Este é o único arquivo da coluna que conhece os dois lados, e ele é fino de propósito:
 * tudo o que decide **o que a fórmula vale** está no avaliador, testado sem navegador; aqui
 * só se resolve *"de onde vêm as colunas e as células"*.
 *
 * ## O cache, e por que ele é do PARSER e não do resultado
 *
 * `analisar()` roda uma vez por (expressão, catálogo); `avaliar()` roda uma vez por linha.
 * Numa tabela de 420 lançamentos com 3 colunas de fórmula, reanalisar por célula seria 1260
 * parses a cada render — e o parser é a parte cara.
 *
 * ⚠️ Já o **resultado** não se guarda. Ele é função de células que mudam, e um cache de
 * resultado precisaria saber quando invalidar — que é exatamente o trabalho que os signals
 * do `@preact/signals-core` já fazem uma camada acima, de graça e sem erro. Guardar seria
 * construir a segunda verdade da Regra #9, e a versão errada dela: a que responde rápido e
 * responde velho.
 */

import type { DataSource } from '@blocksuite/affine/blocks/database';

import {
  analisar, avaliar, ErroDeFormula, rotuloCurto, VAZIO,
  type CatalogoDeColunas, type No, type Valor,
} from './avaliador';
// ⚠️ De `modelo` e NÃO de `define`: `define.ts` importa valor de `@blocksuite/affine`, e
// importá-lo aqui arrastaria o BlockSuite para dentro da ponte — que deixaria de rodar sem
// build, junto com a suíte que a testa.
import {
  ehErroDeFormula, TIPO_DA_COLUNA_DE_FORMULA,
  type FormulaPropertyData, type FormulaRawValue,
} from './modelo';

/** `analisar` é caro; a chave é a expressão MAIS a assinatura do catálogo. */
const cacheDeArvores = new Map<string, { arvore: No } | { erro: ErroDeFormula }>();

/**
 * A assinatura do catálogo entra na chave porque `analisar` resolve nome para id **contra
 * ele**. A mesma expressão em duas tabelas diferentes produz árvores diferentes; sem isto, a
 * segunda tabela receberia a árvore da primeira, com ids de colunas que não existem nela — e
 * o erro apareceria como "coluna não existe mais", apontando para o lugar errado.
 */
function assinaturaDoCatalogo(ds: DataSource): string {
  return ds.properties$.value
    .map(id => `${id}:${ds.propertyNameGet(id)}`)
    .join('|');
}

function catalogoDe(ds: DataSource): CatalogoDeColunas {
  const porNome = new Map<string, string>();
  for (const id of ds.properties$.value) {
    porNome.set(ds.propertyNameGet(id), id);
  }
  return {
    idPorNome: (nome: string) => porNome.get(nome),
    nomes: () => [...porNome.keys()],
  };
}

function arvoreDe(
  expressao: string,
  ds: DataSource
): { arvore: No } | { erro: ErroDeFormula } {
  const chave = `${assinaturaDoCatalogo(ds)} ${expressao}`;
  const guardado = cacheDeArvores.get(chave);
  if (guardado) return guardado;

  let resultado: { arvore: No } | { erro: ErroDeFormula };
  try {
    resultado = { arvore: analisar(expressao, catalogoDe(ds)) };
  } catch (e) {
    resultado = {
      erro: e instanceof ErroDeFormula
        ? e
        : new ErroDeFormula('sintaxe', e instanceof Error ? e.message : String(e)),
    };
  }

  // Um teto para o cache: sem ele, um documento em que alguém edita a expressão letra a letra
  // acumula uma entrada por tecla digitada, para sempre.
  if (cacheDeArvores.size > 512) cacheDeArvores.clear();
  cacheDeArvores.set(chave, resultado);
  return resultado;
}

/** A fórmula de uma coluna, quando ela for de fórmula. `null` para coluna comum. */
function formulaDaColuna(ds: DataSource, propertyId: string): No | null {
  if (ds.propertyTypeGet(propertyId) !== TIPO_DA_COLUNA_DE_FORMULA) return null;
  const dados = ds.propertyDataGet(propertyId) as unknown as FormulaPropertyData;
  const expressao = dados?.expressao?.trim();
  if (!expressao) return null;
  const r = arvoreDe(expressao, ds);
  // Expressão inválida não é dependência — é erro dela própria, e será reportado quando
  // aquela coluna for calculada. Devolver `null` aqui evita que o erro de UMA coluna se
  // propague como erro de TODAS as que a referenciam.
  return 'arvore' in r ? r.arvore : null;
}

/**
 * O valor de uma célula de fórmula. É o que `spacialValueGet` devolve.
 *
 * Nunca lança: devolve o objeto de erro, que a célula mostra como rótulo curto. Lançar aqui
 * subiria por `cellValueGet` para `value$` (um `computed`) e daí para o render, derrubando a
 * tabela inteira por causa de uma linha.
 */
export function calcularCelula(
  ds: DataSource,
  rowId: string,
  propertyId: string,
  agora: number = Date.now()
): FormulaRawValue {
  const dados = ds.propertyDataGet(propertyId) as unknown as FormulaPropertyData;
  const expressao = dados?.expressao?.trim();
  if (!expressao) return null;

  const r = arvoreDe(expressao, ds);
  if ('erro' in r) return comoErro(r.erro);

  try {
    const valor = avaliar(r.arvore, {
      agora,
      locale: dados.locale || 'pt-BR',
      propertyIdAtual: propertyId,
      // ⚠️ `cellValueGet` e não `getCell`: a célula de origem pode ser ela própria derivada
      // (`title`, `created-time`, ou outra fórmula), e só `cellValueGet` sabe disso.
      valorDaCelula: (id: string) =>
        (ds.properties$.value.includes(id) ? ds.cellValueGet(rowId, id) : undefined),
      formulaDaColuna: (id: string) => formulaDaColuna(ds, id),
    });
    return valor === VAZIO ? null : (valor as Exclude<Valor, symbol>);
  } catch (e) {
    return comoErro(e);
  }
}

function comoErro(e: unknown): FormulaRawValue {
  const err = e instanceof ErroDeFormula
    ? e
    : new ErroDeFormula('sintaxe', e instanceof Error ? e.message : String(e));
  return {
    __erroDeFormula: true,
    codigo: err.codigo,
    mensagem: err.message,
    rotulo: rotuloCurto(err),
  };
}

export { ehErroDeFormula };

/**
 * A entrada que se registra em `DatabaseBlockDataSource.externalSpacialProperties`.
 *
 * ⚠️ `valueSet` é no-op — a coluna é derivada.
 *
 * ⚠️ E o **terceiro argumento** de `valueGet` não é enfeite: as quatro implementações do
 * upstream são arrow functions no inicializador do campo, então capturam `this` da instância
 * de graça. A nossa chega de fora, como objeto solto — não há `this` para capturar, e
 * `spacialProperties[tipo].valueGet(...)` ligaria `this` ao objeto `spacialProperties`, não
 * ao dataSource. Foi por isso que o patch passou a repassá-lo explicitamente.
 */
export function propriedadeEspecialDaFormula() {
  return {
    valueSet: () => {},
    valueGet: (rowId: string, propertyId: string, dataSource: DataSource) =>
      calcularCelula(dataSource, rowId, propertyId),
  };
}
